import React, { ReactNode } from 'react';
import './text-input.scss';

export interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  /** `sm` (28px) fits a table cell; `md` (34px) is the field size. */
  size?: 'sm' | 'md';
  /** Monospace with tabular figures, for job numbers, stage ids, SI numbers. */
  mono?: boolean;
  /** An adornment inside the right edge: a save dot, an availability tick. */
  trailing?: ReactNode;
}

/**
 * The text/number/date input of the form primitives. Spreads every native
 * prop through, so the uncontrolled `defaultValue` + `onBlur` cells in the
 * drawing and programme tables keep working unchanged.
 *
 * forwardRef rather than React.FC: callers need the element to focus it.
 */
const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ invalid = false, size = 'md', mono = false, trailing, className = '', ...rest }, ref) => {
    const input = (
      <input
        ref={ref}
        className={`text-input text-input--${size} ${mono ? 'text-input--mono' : ''} ${invalid ? 'is-invalid' : ''} ${trailing ? 'has-trailing' : ''} ${className}`.trim()}
        aria-invalid={invalid || undefined}
        {...rest}
      />
    );
    if (!trailing) return input;
    return (
      <span className="text-input-wrap">
        {input}
        <span className="text-input-wrap__trailing">{trailing}</span>
      </span>
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
